/**
 * GpuParticlePool — owns the GPU storage buffers + compute kernels for one
 * particle effect, and drives spawn/update dispatches.
 *
 * Capacity is fixed at create and rounded up to a power of two. Free-slot
 * management is a CPU-side monotonic ring cursor (see ringBuffer.ts) — no GPU
 * atomics in v1. The pool exposes the position buffer as a vertex attribute
 * (`positionAttribute()`) so the render material can read per-instance state.
 *
 * Dispatch model (all fire-and-forget via `renderer.computeAsync`, matching
 * RainComputeEffect):
 *   • `clear()`   — once at init, zeroes + parks all slots dead.
 *   • `spawn(n)`  — CPU allocates `n` slots as 1–2 contiguous ranges, sets the
 *                   spawn uniforms per range, and dispatches the spawn kernel
 *                   once per range with `count = range.count`.
 *   • `update()`  — ages + integrates every slot (count = capacity).
 */

import type * as THREE from 'three/webgpu';
import type { EngineRenderer } from '../../render/Renderer.js';
import { Logger } from '../../core/Logger.js';
import { createParticleKernels, type ParticleKernels } from './kernels.js';
import { SpawnRingBuffer, nextPow2 } from './ringBuffer.js';
import { shapeCodeOf, type GpuShapeCode, type GpuParticleConfig } from './types.js';
import type { EmitterShape } from '../types.js';

export interface GpuParticlePoolOptions {
  /** Requested capacity (rounded up to pow2). */
  capacity: number;
}

export class GpuParticlePool {
  /** Power-of-two slot capacity. */
  readonly capacity: number;
  private renderer: EngineRenderer;
  private kernels: ParticleKernels;
  private ring: SpawnRingBuffer;
  private _frameSeed = 1;
  private _disposed = false;
  /**
   * Running estimate of alive particles. Incremented by spawn batches and
   * decremented by an analytic decay each update tick (we can't cheaply read
   * back GPU death without a readback pass). Clamped to [0, capacity].
   */
  private _activeEstimate = 0;
  /** Average lifetime (s), used to decay the active estimate. */
  private _avgLifetime = 2;

  constructor(renderer: EngineRenderer, options: GpuParticlePoolOptions) {
    this.renderer = renderer;
    this.capacity = nextPow2(options.capacity);
    this.kernels = createParticleKernels({ capacity: this.capacity });
    this.ring = new SpawnRingBuffer(this.capacity);
  }

  /** Position storage as a vertex attribute (vec4 px,py,pz,age). */
  positionAttribute(): unknown {
    return this.kernels.posBuffer.toAttribute();
  }

  /** Velocity storage as a vertex attribute (vec4 vx,vy,vz,size). */
  velocityAttribute(): unknown {
    return this.kernels.velBuffer.toAttribute();
  }

  /** Misc storage as a vertex attribute (vec4 life,seed,startSize,startSpeed). */
  miscAttribute(): unknown {
    return this.kernels.miscBuffer.toAttribute();
  }

  /** Best-effort count of currently-alive particles. */
  get activeEstimate(): number {
    return Math.round(this._activeEstimate);
  }

  /** Initialise all slots to a dead/parked state. Call once after create. */
  clear(): void {
    if (this._disposed) return;
    this.ring.reset();
    this._activeEstimate = 0;
    this.renderer.native.computeAsync(this.kernels.clearNode as never);
  }

  /**
   * Push the emitter's shape/speed/lifetime/size config into the spawn
   * uniforms. Call whenever the config changes (cheap; just uniform writes).
   */
  configureSpawn(config: GpuParticleConfig): void {
    const u = this.kernels.spawnUniforms;
    const shape: EmitterShape = config.shape ?? { type: 'point' };
    setU(u.shape, shapeCodeOf(shape));

    // Shape params (defaults keep unused fields harmless).
    let radius = 1, height = 1, angle = Math.PI / 6, bx = 1, by = 1, bz = 1;
    switch (shape.type) {
      case 'sphere': radius = shape.radius; break;
      case 'hemisphere': radius = shape.radius; break;
      case 'circle': radius = shape.radius; break;
      case 'cone': radius = shape.radius; height = shape.height; angle = shape.angle; break;
      case 'box': bx = shape.halfExtents.x; by = shape.halfExtents.y; bz = shape.halfExtents.z; break;
      default: break;
    }
    setU(u.shapeRadius, radius);
    setU(u.shapeHeight, height);
    setU(u.shapeAngle, angle);
    setU(u.boxX, bx); setU(u.boxY, by); setU(u.boxZ, bz);

    const [spMin, spMax] = rangeOf(config.startSpeed);
    const [lfMin, lfMax] = rangeOf(config.lifetime);
    const [szMin, szMax] = rangeOf(config.startSize);
    setU(u.speedMin, spMin); setU(u.speedMax, spMax);
    setU(u.lifeMin, lfMin); setU(u.lifeMax, lfMax);
    setU(u.sizeMin, szMin); setU(u.sizeMax, szMax);

    // Track an analytic active-particle estimate from average lifetime.
    this._avgLifetime = Math.max(0.0001, (lfMin + lfMax) * 0.5);
  }

  /** Push the per-frame force uniforms into the update kernel. */
  configureForces(config: GpuParticleConfig): void {
    const u = this.kernels.updateUniforms;
    const forces = config.forces;
    const gravMod = forces?.gravityModifier ?? config.gravityModifier ?? 0;
    setU(u.gravity, gravMod * -9.81);
    setU(u.drag, forces?.drag ?? 0);
    if (forces?.curl) {
      setU(u.curlStrength, forces.curl.strength);
      setU(u.curlFrequency, forces.curl.frequency);
      setU(u.curlScroll, forces.curl.scrollSpeed);
    } else {
      setU(u.curlStrength, 0);
    }
    if (forces?.attractor) {
      setU(u.attractorX, forces.attractor.point.x);
      setU(u.attractorY, forces.attractor.point.y);
      setU(u.attractorZ, forces.attractor.point.z);
      setU(u.attractorStrength, forces.attractor.strength);
    } else {
      setU(u.attractorStrength, 0);
    }
  }

  /**
   * Spawn `n` particles starting at the ring cursor. Dispatches the spawn
   * kernel once per contiguous range (1–2). `origin` is the emitter world
   * position used to place spawns (world-space simulation); `basis`, when
   * given, is the emitter's world matrix, whose upper 3×3 rotates the
   * emitter-local shape samples (omit it for an unrotated emitter).
   */
  spawn(n: number, origin: { x: number; y: number; z: number }, basis?: THREE.Matrix4 | null): void {
    if (this._disposed || n <= 0) return;
    const ranges = this.ring.allocate(n);
    if (ranges.length === 0) return;

    const u = this.kernels.spawnUniforms;
    setU(u.originX, origin.x);
    setU(u.originY, origin.y);
    setU(u.originZ, origin.z);
    // Matrix4.elements is COLUMN-major: 0..2 is the x axis, 4..6 the y, 8..10
    // the z. Identity when no basis was supplied.
    const e = basis?.elements;
    setU(u.basis0X, e ? e[0] : 1); setU(u.basis0Y, e ? e[1] : 0); setU(u.basis0Z, e ? e[2] : 0);
    setU(u.basis1X, e ? e[4] : 0); setU(u.basis1Y, e ? e[5] : 1); setU(u.basis1Z, e ? e[6] : 0);
    setU(u.basis2X, e ? e[8] : 0); setU(u.basis2Y, e ? e[9] : 0); setU(u.basis2Z, e ? e[10] : 1);

    let spawned = 0;
    for (const range of ranges) {
      setU(u.spawnStart, range.start);
      setU(u.spawnCount, range.count);
      setU(u.frameSeed, this._frameSeed);
      // Resize the dispatch to exactly this range and fire.
      (this.kernels.spawnNode as { count: number }).count = range.count;
      this.renderer.native.computeAsync(this.kernels.spawnNode as never);
      // Advance the frame seed between ranges so the two halves don't share
      // the same hash sequence on a wrapped batch.
      this._frameSeed = (this._frameSeed + range.count + 1) >>> 0;
      spawned += range.count;
    }

    this._activeEstimate = Math.min(this.capacity, this._activeEstimate + spawned);
  }

  /**
   * Advance the simulation by one frame. Uses TSL `deltaTime` for dt inside
   * the kernel; the CPU `dt` here only drives the active-particle estimate
   * decay. `time` is pushed for curl-noise scrolling.
   */
  update(dt: number, elapsed: number): void {
    if (this._disposed) return;
    setU(this.kernels.updateUniforms.time, elapsed);
    this.renderer.native.computeAsync(this.kernels.updateNode as never);

    // Analytic decay of the alive estimate: a particle dies after ~avgLifetime
    // seconds, so the fraction dying this frame ≈ dt / avgLifetime.
    if (dt > 0 && this._activeEstimate > 0) {
      this._activeEstimate = Math.max(0, this._activeEstimate - this._activeEstimate * (dt / this._avgLifetime));
    }
  }

  dispose(): void {
    if (this._disposed) return;
    this._disposed = true;
    disposeBuffer(this.kernels.posBuffer);
    disposeBuffer(this.kernels.velBuffer);
    disposeBuffer(this.kernels.miscBuffer);
    Logger.debug('GpuParticlePool', `Disposed (capacity=${this.capacity})`);
  }
}

// ─── helpers ─────────────────────────────────────────────────────────────────

/** Write a value into a TSL uniform node's `.value`. */
function setU(node: unknown, value: number | GpuShapeCode): void {
  (node as { value: number }).value = value as number;
}

/** Normalise a number | {min,max} to a [min, max] tuple. */
function rangeOf(v: number | { min: number; max: number }): [number, number] {
  if (typeof v === 'number') return [v, v];
  return [v.min, v.max];
}

function disposeBuffer(buf: unknown): void {
  const d = (buf as { dispose?: () => void }).dispose;
  if (typeof d === 'function') {
    try { d.call(buf); } catch { /* ignore */ }
  }
}
